import type { FC } from "react";
import {
	ClientPortfolioInvestmentFrequencyEnum,
	ClientPortfolioToolIdentificationTypeEnum,
	ClientPortfolioToolTypeEnum,
	CurrencyEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { ToolRowCommonProps } from "web_myplann/Portfolio/Helpers/investmentToolTypes";
import { useToolSummary } from "web_myplann/Portfolio/Helpers/useToolSummary";
import { toolHasActivePeriodicPurchaseInstructions } from "web_myplann/Portfolio/Helpers/utils";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import { moneyFormatter } from "web_myplann/Shared/Utils/formatters";
import { ToolRowWrapper } from "./ToolRowWrapper";

const PageResources = Resources.PortfolioDetail;

export const InvestmentToolRow: FC<ToolRowCommonProps> = ({
	tool,
	toolIdx,
	portfolioId,
	toolsSummary,
	expanded,
	onToggle,
	noDivider,
}) => {
	const { t } = useResource();
	const { totalValue, totalInvested } = useToolSummary(tool.id, toolsSummary);

	const item = tool.investments?.[0];
	if (!item) return null;

	const hasActivePurchases = toolHasActivePeriodicPurchaseInstructions(tool);
	const shouldShowRecurringPayment =
		hasActivePurchases && tool.monthlyPayment > 0;

	const getRecurringValue = () => {
		if (tool.monthlyPayment === 0) {
			return null;
		}
		const currency = t(
			Resources.PortfolioDetail.SharedLabels.Currency[CurrencyEnum.CZK],
		);
		return `${moneyFormatter(tool.monthlyPayment, currency, undefined, 0)} ${t(
			PageResources.ToolHistory.Fund.Frequency[
				ClientPortfolioInvestmentFrequencyEnum.Monthly
			],
		).toLowerCase()}`;
	};

	return (
		<ToolRowWrapper
			label={tool.name ?? ""}
			totalValue={totalValue}
			expanded={expanded}
			onToggle={onToggle}
			noDivider={noDivider}
			portfolioId={portfolioId}
			toolIdx={toolIdx}
		>
			<DataRow
				label={t(PageResources.LifeInsurance.InvestmentTool.type)}
				value={t(
					PageResources.LifeInsurance.InvestmentTool.Type[
						tool.type ?? ClientPortfolioToolTypeEnum.Other
					],
				)}
			/>
			<DataRow
				label={t(
					PageResources.LifeInsurance.InvestmentTool.IdentificationType[
						tool.identificationType ??
							ClientPortfolioToolIdentificationTypeEnum.None
					],
				)}
				value={tool.identification}
			/>
			{shouldShowRecurringPayment && (
				<DataRow
					label={t(PageResources.Investment.InvestmentTool.reocurringPayment)}
					value={getRecurringValue()}
				/>
			)}
			<DataRow
				label={t(PageResources.Investment.InvestmentTool.invested)}
				value={totalInvested}
			/>
			<DataRow
				label={t(PageResources.Investment.InvestmentTool.value)}
				value={totalValue}
			/>
		</ToolRowWrapper>
	);
};
